import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";

import { useNavigate, useSearchParams } from 'react-router-dom';

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "name-asc", label: "Name: A to Z" },
];

export default function SidebarFilter({ onChange, mobileOpen, onClose, sortBy, onSortChange }) {
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [categories, setCategories] = useState([]);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentCategory = searchParams.get("category") || "";

  // Handle click outside for sort dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch categories from products
  useEffect(() => {
    async function fetchCategories() {
      try {
        const snap = await getDocs(collection(db, "products"));
        const cats = new Set();
        snap.docs.forEach((doc) => {
          const cat = doc.data().category;
          if (cat && cat.trim()) {
            cats.add(cat.trim());
          }
        });
        setCategories(Array.from(cats).sort());
      } catch (e) {
        console.error("Error fetching categories:", e);
      }
    }
    fetchCategories();
  }, []);

  const apply = () => {
    onChange &&
      onChange({
        min: min ? Number(min) : undefined,
        max: max ? Number(max) : undefined,
      });
    if (onClose) onClose();
  };

  const reset = () => {
    setMin("");
    setMax("");
    onChange && onChange({});
    if (onClose) onClose();
  };

  const handleCategoryClick = (cat) => {
    if (cat === currentCategory) {
      // Already selected, go to all collections
      navigate("/collections");
    } else {
      navigate(`/collections?category=${encodeURIComponent(cat)}`);
    }
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`mobile-filter-overlay ${mobileOpen ? 'open' : ''}`}
        onClick={onClose}
      />

      <div className={`sidebar-filter p-4 ${mobileOpen ? 'open' : ''}`} style={{ backgroundColor: '#ffffff', borderRadius: '20px', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>
        <div className="d-flex justify-content-between align-items-center mb-4 pb-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div className="d-flex align-items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
            <div className="text-dark fw-bold fs-5" style={{ fontFamily: 'var(--font-serif)' }}>Filters</div>
          </div>
          {/* Close button for mobile */}
          <div className="mobile-only sidebar-close-btn text-dark" onClick={onClose} style={{ cursor: 'pointer' }}>
            ✕
          </div>
        </div>

        {/* Sort Section */}
        <div className="filter-section mb-4">
          <div className="fw-bold mb-3 text-uppercase" style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '1px' }}>Sort By</div>
          <div className="creative-sort-container" ref={sortDropdownRef}>
            <div
              className={`creative-sort-trigger ${isSortOpen ? 'open' : ''}`}
              onClick={() => setIsSortOpen(!isSortOpen)}
              style={{ borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', padding: '12px 16px', transition: 'all 0.3s ease', cursor: 'pointer' }}
            >
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontWeight: '600', color: '#1e293b' }}>{sortOptions.find(o => o.value === sortBy)?.label || 'Newest First'}</span>
              </div>
              <svg className={`sort-chevron ${isSortOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <div className={`creative-sort-menu ${isSortOpen ? 'open' : ''}`}>
              {sortOptions.map((option) => (
                <div
                  key={option.value}
                  className={`creative-sort-option ${sortBy === option.value ? 'selected' : ''}`}
                  onClick={() => {
                    onSortChange && onSortChange(option.value);
                    setIsSortOpen(false);
                  }}
                >
                  <span className="sort-label">{option.label}</span>
                  {sortBy === option.value && (
                    <svg className="check-icon ms-auto" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Categories Section */}
        <div className="filter-section mb-4">
          <div className="fw-bold mb-3 text-uppercase" style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '1px' }}>Categories</div>
          <div className="category-filter-list d-flex flex-column gap-2">
            <div
              onClick={() => { navigate("/collections"); if (onClose) onClose(); }}
              style={{ padding: '10px 16px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: !currentCategory ? 'var(--primary)' : 'transparent', color: !currentCategory ? '#fff' : '#475569', fontWeight: !currentCategory ? '600' : '500' }}
              onMouseEnter={(e) => { if (currentCategory) e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={(e) => { if (currentCategory) e.currentTarget.style.background = 'transparent'; }}
            >
              <span>All Collections</span>
              {!currentCategory && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
            </div>
            {categories.map((cat) => (
              <div
                key={cat}
                onClick={() => handleCategoryClick(cat)}
                style={{ padding: '10px 16px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: currentCategory === cat ? 'var(--primary)' : 'transparent', color: currentCategory === cat ? '#fff' : '#475569', fontWeight: currentCategory === cat ? '600' : '500' }}
                onMouseEnter={(e) => { if (currentCategory !== cat) e.currentTarget.style.background = '#f1f5f9'; }}
                onMouseLeave={(e) => { if (currentCategory !== cat) e.currentTarget.style.background = 'transparent'; }}
              >
                <span>{cat}</span>
                {currentCategory === cat && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
              </div>
            ))}
          </div>
        </div>

        {/* Price Range Section */}
        <div className="filter-section mb-4">
          <div className="fw-bold mb-3 text-uppercase" style={{ color: '#94a3b8', fontSize: '0.75rem', letterSpacing: '1px' }}>Price Range</div>
          <div className="d-flex align-items-center gap-2">
            <div className="position-relative w-50">
              <span className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.9rem' }}>₹</span>
              <input
                type="number"
                placeholder="Min"
                value={min}
                onChange={(e) => setMin(e.target.value)}
                className="form-control shadow-none border-0"
                style={{ borderRadius: '10px', background: '#f8fafc', color: '#334155', padding: '10px 10px 10px 24px', fontWeight: '500' }}
              />
            </div>
            <span style={{ color: '#cbd5e1' }}>-</span>
            <div className="position-relative w-50">
              <span className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.9rem' }}>₹</span>
              <input
                type="number"
                placeholder="Max"
                value={max}
                onChange={(e) => setMax(e.target.value)}
                className="form-control shadow-none border-0"
                style={{ borderRadius: '10px', background: '#f8fafc', color: '#334155', padding: '10px 10px 10px 24px', fontWeight: '500' }}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="filter-actions d-flex gap-3 mt-4 pt-4" style={{ borderTop: '1px solid #f1f5f9' }}>
          <button
            className="btn flex-fill fw-bold border-0"
            style={{ backgroundColor: '#f1f5f9', color: '#475569', borderRadius: '12px', padding: '12px 0', transition: 'all 0.3s ease' }}
            onClick={reset}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
          >
            Reset
          </button>
          <button
            className="btn btn-primary flex-fill fw-bold border-0"
            style={{ borderRadius: '12px', padding: '12px 0', background: 'linear-gradient(135deg, var(--primary), var(--primary-hover))', boxShadow: '0 4px 15px rgba(198, 40, 40, 0.25)' }}
            onClick={apply}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}

